<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Tag } from "vant";
import UserAvatar from "@/components/UserAvatar.vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { useAllVisibility } from "@/use/common";
import { useMate } from "@/stores/mate";
import { MediaUploadFileItem } from "@/types/component";
import PlusIcon from "@/components/icon/PlusIcon.vue";
import XmarkCircleIcon from "@/components/icon/XmarkCircleIcon.vue";
import { SharedMessageItem } from "@/components/mobile/overlay/ChatMessageShareSelection.vue";
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";

import { useStory } from "@/stores/story";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import { MAX_IMG_COUNT } from "@/components/mobile/post/config";
import { useGlobalModal } from "@/stores/globalModal";
import { Mate, Media, Story, Visibility } from "@/types/entity";
import { AIWriteType } from "@/enums/mateEnums";
import { removeHtmlTag } from "@/tools/text";

export interface IThreadPostItem {
  message: string;
  audioMedia?: Media;
  imgList: MediaUploadFileItem[];
  videoList: MediaUploadFileItem[];
  sharedMessageList: SharedMessageItem[];
  visibility: Visibility;
  mentionMateIdzz: Pick<Mate, "name" | "uuid">[];
  storyId?: number;
  isAiGenerated?: boolean;
}

// 常量
// const MAX_MESSAGE_LENGTH = 2000;
// const MESSAGE_PLACE_HOLDER = i18n.global.t("Moment.todayHappened");
// Props
const props = withDefaults(
  defineProps<{
    enableVisibilitySelection?: boolean;
    enableDelete?: boolean;
    item: IThreadPostItem;
    isEditing?: boolean;
    showAddBtn?: boolean;
  }>(),
  {
    enableVisibilitySelection: true,
    enableDelete: true,
    isEditing: true,
    showAddBtn: true,
  }
);

const emit = defineEmits<{
  (e: "update:item", item: IThreadPostItem): void;
  (e: "triggerImgUpload"): void;
  (e: "triggerVideoUpload"): void;
  (e: "showGenGallery"): void;
  (e: "triggerChatShare"): void;
  (e: "triggerVisibilitySelect"): void;
  (e: "deleteItem"): void;
  (e: "addItem"): void;
  (e: "triggerEdit"): void;
  (e: "triggerSelectedMates"): void;
}>();

// 内部状态
const mateStore = useMate();
const storyStore = useStory();

const visibilities = computed<
  {
    text: string;
    desc?: string;
    value: Visibility;
  }[]
>(useAllVisibility);
const visibilityText = computed(() => {
  return visibilities.value.find((v) => v.value === props.item.visibility)
    ?.text;
});
const isNotEmpty = computed(() => {
  return (
    props.item.message.length > 0 ||
    props.item.imgList.length > 0 ||
    props.item.videoList.length > 0 ||
    props.item.sharedMessageList.length > 0
  );
});
const story = ref<Story | null>(null);
watch(
  () => props.item.storyId,
  async (val) => {
    if (!val) {
      story.value = null;
      return;
    }
    const storyList = await storyStore.fetchStoryList({
      storyId: props.item.storyId,
    });
    if (storyList.length > 0) {
      story.value = storyList[0];
    }
  },
  { immediate: true }
);

const deleteAtMate = (mate: Pick<Mate, "name" | "uuid">) => {
  let atMateArr = props.item.mentionMateIdzz;

  atMateArr = atMateArr.filter((item) => item.uuid != mate.uuid);

  emit("update:item", { ...props.item, mentionMateIdzz: atMateArr });
};
const clickPreviewImg = (idx: number) => {
  useGlobalModal().openMediaPreviewModal(
    props.item.imgList.map((img) => img.url!),
    idx
  );
};

const handleGenerate = (media?: Media) => {
  emit("update:item", { ...props.item, audioMedia: media });
};

const removeImgMedia = (idx: number) => {
  const item = props.item;
  item.imgList.splice(idx, 1);
};

const removeVideoMedia = (idx: number) => {
  const item = props.item;
  item.videoList.splice(idx, 1);
};

const modalStore = useGlobalModal();
const clickFillMoment = () => {
  const { item } = props;
  modalStore.openRichEditor({
    title: "编辑随记",
    content: item.message,
    aiType: AIWriteType.Moment,
    callback: (content) => {
      // 做一个截流
      item.message = content!;
    },
    options: {
      mateId: mateStore.currentMate!.id,
    },
  });
};
</script>
<template>
  <div class="flex flex-col gap-2">
    <!-- Mate -->
    <div class="grid grid-cols-[auto_1fr] gap-2 relative overflow-hidden">
      <div class="flex flex-col gap-2 w-10 h-fit">
        <!-- 头像 -->
        <user-avatar
          :avatar-url="mateStore.currentMate!.mediaUrl"
          :mate="mateStore.currentMate!"
          class="w-10"
        />
        <span class="text-xs text-base-content/50 line-clamp-2 text-center">
          {{ mateStore.currentMate!.name }}
        </span>
      </div>
      <!-- 输入域 -->
      <div class="w-full flex flex-col flex-grow gap-2 h-fit overflow-hidden">
        <div
          v-if="enableVisibilitySelection && isEditing"
          class="flex items-center"
        >
          <button
            class="btn btn-xs rounded-2xl w-fit btn-primary-content"
            @click="emit('triggerVisibilitySelect')"
          >
            {{ visibilityText }}
            <light-arrow-icon class="w-3 h-3 -rotate-90" />
          </button>
          <span class="text-xs text-base-content/50 line-clamp-1">
            {{
              useAllVisibility().find((v) => v.value === item.visibility)?.desc
            }}
          </span>
        </div>
        <div
          class="prose prose-sm"
          v-html="item.message"
          @click.stop="clickFillMoment"
        ></div>

        <button
          class="btn btn-primary btn-sm w-full"
          @click="clickFillMoment"
          v-if="!removeHtmlTag(item.message)"
        >
          点击填写随记
        </button>

        <VoiceGenerate
          :mate="mateStore.currentMate!"
          :text="item.message"
          tone-type="voice"
          :active-media="item.audioMedia"
          @generate="handleGenerate"
        />

        <div class="flex justify-end">
          <div class="flex items-center gap-2">
            <span class="text-xs">
              {{ $t("Message.contentIsAiGenerated") }}
            </span>
            <VanSwitch
              :model-value="item.isAiGenerated"
              size="16"
              @change="emit('update:item', { ...item, isAiGenerated: $event })"
            />
          </div>
        </div>
        <div v-if="story" class="flex flex-col relative">
          <story-list-item :story="story" class="border px-2 rounded" />
          <button
            class="absolute right-2 top-2"
            @click="
              $event.stopPropagation();
              emit('update:item', {
                ...item,
                storyId: undefined,
                videoList: [],
              });
            "
          >
            <xmark-circle-icon class="w-4 text-base-content/50" />
          </button>
        </div>
        <div
          v-if="item.sharedMessageList && item.sharedMessageList.length > 0"
          class="flex-col max-h-64 overflow-hidden border p-2 rounded relative thread-chat-share-item bg-base-200"
          @click="emit('triggerChatShare')"
        >
          <chat-message-item
            v-for="msg in item.sharedMessageList.slice(0, 4)"
            :key="msg.id"
            :avatar-url="msg.mediaUrl"
            :msg="msg.msg"
            :name="msg.name"
            :position="msg.isCurrentUser ? 'end' : 'start'"
            class="flex-grow"
          />
          <button
            class="absolute right-2 top-2"
            @click="
              $event.stopPropagation();
              emit('update:item', { ...item, sharedMessageList: [] });
            "
          >
            <xmark-circle-icon class="w-4 text-base-content/50" />
          </button>
        </div>
        <div
          v-if="item.mentionMateIdzz && item.mentionMateIdzz.length > 0"
          class="flex gap-1 flex-wrap"
        >
          <Tag
            v-for="atMate in item.mentionMateIdzz"
            :key="atMate.uuid"
            class="badge badge-neutral"
            closeable
            @click="deleteAtMate(atMate)"
            @close="deleteAtMate(atMate)"
          >
            {{ atMate.name }}
          </Tag>
        </div>
      </div>
      <button
        v-show="enableDelete && isEditing"
        class="absolute right-2 top-2"
        @click="emit('deleteItem')"
      >
        <xmark-circle-icon class="w-4 text-base-content/50" />
      </button>
    </div>

    <div class="flex gap-2 flex-wrap">
      <template v-if="item.imgList.length > 0">
        <div
          v-for="(img, idx) in item.imgList"
          :key="img.url"
          class="flex gap-4 relative"
        >
          <div
            class="absolute top-0 right-0 rounded-full bg-base-content/50 flex justify-center items-center w-4 h-4 translate-x-1/2 -translate-y-1/2"
            @click="removeImgMedia(idx)"
          >
            <i class="iconify mdi--close text-base-100 w-3" />
          </div>
          <img
            :src="img.url"
            alt=""
            class="h-[80px]"
            @click="clickPreviewImg(idx)"
            crossorigin="anonymous"
          />
        </div>
      </template>
      <template v-if="item.videoList.length > 0">
        <div
          v-for="(video, idx) in item.videoList"
          :key="video.url"
          class="flex gap-4 relative"
        >
          <div
            class="absolute top-0 right-0 rounded-full bg-base-content/50 flex justify-center items-center w-4 h-4 translate-x-1/2 -translate-y-1/2"
            @click="removeVideoMedia(idx)"
          >
            <i class="iconify mdi--close text-base-content/50 w-3" />
          </div>
          <video
            :src="video.url"
            alt=""
            autoplay
            class="h-[80px]"
            muted
            crossorigin="anonymous"
          />
        </div>
      </template>
    </div>

    <!-- 按钮组 -->
    <div v-show="isEditing" class="border-t pt-3 w-full p-2">
      <div class="flex justify-between">
        <div class="flex items-center gap-2">
          <button
            :disabled="
              item.imgList.length >= MAX_IMG_COUNT || item.videoList.length > 0
            "
            class="btn btn-sm btn-ghost btn-square"
            @click="emit('triggerImgUpload')"
          >
            <i class="iconify mingcute--pic-line" />
          </button>
          <button
            :disabled="item.videoList.length >= 1 || item.imgList.length > 0"
            class="btn btn-sm btn-ghost btn-square"
            @click="emit('triggerVideoUpload')"
          >
            <i class="iconify mingcute--video-line" />
          </button>
          <button
            v-if="false"
            class="btn btn-sm btn-ghost btn-square"
            @click="emit('triggerSelectedMates')"
          >
            <i
              :class="{ 'text-primary': item.mentionMateIdzz.length > 0 }"
              class="iconify mingcute--at-line"
            />
          </button>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-show="showAddBtn"
            :disabled="!isNotEmpty"
            class="btn btn-xs btn-circle btn-primary text-base-100"
            @click="emit('addItem')"
          >
            <plus-icon class="icon-sm" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.thread-textarea.not-editing textarea {
  @apply text-base-content/50;
}
</style>
